import React from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { Stack, Button, Alert, Progress, Input } from '@/components/ui';
import { LogOut, CheckCircle2, Share2, Link, FileText, BookOpen, Package, Megaphone, Presentation, Play, Palette, GraduationCap, MoreHorizontal, User } from 'lucide-react';
import { OrgKnowledgeService } from '@/services/OrgKnowledgeService';
import { OrgService } from '@/services/OrgService';
import { BillingService, type BillingPlanDto, type SubscriptionSnapshotDto } from '@/services/BillingService';
import { getActiveOrgId, useOrgStore } from '@/stores/orgStore';
import { useAuthStore } from '@/stores/authStore';
import { apiFetch } from '@/lib/api';

// Steps: profile (for passwordless users) → use-cases → plan-selection (non-alpha) → welcome → done
type Step = 'profile' | 'use-cases' | 'plan-selection' | 'welcome' | 'done';

interface UseCase {
  id: string;
  label: string;
  description: string | null;
  icon: string | null;
  enabled: boolean;
}

// Map icon names to Lucide components
const iconMap: Record<string, React.FC<{ className?: string }>> = {
  Share2, Link, FileText, BookOpen, Package, Megaphone, Presentation, Play, Palette, GraduationCap, MoreHorizontal,
};

const IS_ALPHA = import.meta.env.VITE_IS_ALPHA === 'true';

const SUBSCRIPTION_ACTIVE_STATUSES = ['trialing', 'active', 'past_due'];

function formatCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 0 }).format(amount / 100);
}

function formatNumber(value: number) {
  return new Intl.NumberFormat('en-US').format(value);
}

// Helper to get/set onboarding progress in localStorage
function getStoredProgress(orgId: string | null): { step: Step; selectedUseCases: string[]; websiteUrl: string } | null {
  if (!orgId) return null;
  try {
    const stored = localStorage.getItem(`cascady_onboarding_${orgId}`);
    if (stored) return JSON.parse(stored);
  } catch {}
  return null;
}

function setStoredProgress(orgId: string | null, data: { step: Step; selectedUseCases: string[]; websiteUrl: string }) {
  if (!orgId) return;
  try {
    localStorage.setItem(`cascady_onboarding_${orgId}`, JSON.stringify(data));
  } catch {}
}

function clearStoredProgress(orgId: string | null) {
  if (!orgId) return;
  try {
    localStorage.removeItem(`cascady_onboarding_${orgId}`);
  } catch {}
}

export function Onboarding() {
  const navigate = useNavigate();
  const location = useLocation();
  const billingService = React.useMemo(() => new BillingService(), []);
  const knowledgeService = React.useMemo(() => new OrgKnowledgeService(), []);
  const orgService = React.useMemo(() => new OrgService(), []);

  const initialized = useOrgStore((s) => s.initialized);
  const fetchOrgs = useOrgStore((s) => s.fetchOrgs);
  const orgs = useOrgStore((s) => s.orgs);
  const activeOrgId = useOrgStore((s) => s.activeOrgId);
  const user = useAuthStore((s) => s.user);

  // Detect if user is passwordless (created via anonymous checkout)
  const [needsProfile, setNeedsProfile] = React.useState(false);
  const [profileChecked, setProfileChecked] = React.useState(false);

  // Initialize state from localStorage if available
  const initialProgress = React.useMemo(() => getStoredProgress(activeOrgId), [activeOrgId]);

  const [step, setStepInternal] = React.useState<Step>(() => {
    const stored = initialProgress?.step as string;
    if (stored === 'welcome') return 'welcome';
    if (stored === 'plan-selection') return 'plan-selection';
    if (stored === 'use-cases') return 'use-cases';
    if (stored === 'profile') return 'profile';
    // Default will be set after profile check
    return 'use-cases';
  });

  const [plans, setPlans] = React.useState<BillingPlanDto[]>([]);
  const [plansLoading, setPlansLoading] = React.useState(true);
  const [plansError, setPlansError] = React.useState<string | null>(null);
  const [selectedPlanKey, setSelectedPlanKey] = React.useState<string | null>(null);
  const [selectedInterval, setSelectedInterval] = React.useState<'month' | 'year'>('month');

  const [subscription, setSubscription] = React.useState<SubscriptionSnapshotDto | null>(null);
  const [portalLoading, setPortalLoading] = React.useState(false);

  const [checkoutError, setCheckoutError] = React.useState<string | null>(null);
  const [checkoutMessage, setCheckoutMessage] = React.useState<string | null>(null);
  const [checkoutLoading, setCheckoutLoading] = React.useState(false);

  const [websiteUrl, setWebsiteUrlInternal] = React.useState(initialProgress?.websiteUrl || '');
  const [websiteError, setWebsiteError] = React.useState<string | null>(null);
  const [websiteLoading, setWebsiteLoading] = React.useState(false);
  const [hasExistingWebsite, setHasExistingWebsite] = React.useState(false);

  const [useCases, setUseCases] = React.useState<UseCase[]>([]);
  const [selectedUseCases, setSelectedUseCasesInternal] = React.useState<string[]>(initialProgress?.selectedUseCases || []);
  const [useCasesLoading, setUseCasesLoading] = React.useState(false);
  const [useCasesError, setUseCasesError] = React.useState<string | null>(null);

  // Profile completion state
  const [profileFullName, setProfileFullName] = React.useState('');
  const [profilePassword, setProfilePassword] = React.useState('');
  const [profileConfirmPassword, setProfileConfirmPassword] = React.useState('');
  const [profileOrgName, setProfileOrgName] = React.useState('');
  const [profileLoading, setProfileLoading] = React.useState(false);
  const [profileError, setProfileError] = React.useState<string | null>(null);

  // Check if user needs profile completion (passwordless user from checkout)
  React.useEffect(() => {
    if (!user) return;
    // If user has no name (empty string or null), they likely came from anonymous checkout
    const hasNoName = !user.fullName || user.fullName.trim() === '';
    setNeedsProfile(hasNoName);
    setProfileChecked(true);
    if (hasNoName && step !== 'welcome' && step !== 'done') {
      setStepInternal('profile');
    }
  }, [user]);

  // Wrap setters to persist to localStorage
  const setStep = React.useCallback((newStep: Step) => {
    setStepInternal(newStep);
    setStoredProgress(activeOrgId, { step: newStep, selectedUseCases, websiteUrl });
  }, [activeOrgId, selectedUseCases, websiteUrl]);

  const setWebsiteUrl = React.useCallback((url: string) => {
    setWebsiteUrlInternal(url);
    setStoredProgress(activeOrgId, { step, selectedUseCases, websiteUrl: url });
  }, [activeOrgId, step, selectedUseCases]);

  const setSelectedUseCases = React.useCallback((useCasesOrFn: string[] | ((prev: string[]) => string[])) => {
    setSelectedUseCasesInternal(prev => {
      const newValue = typeof useCasesOrFn === 'function' ? useCasesOrFn(prev) : useCasesOrFn;
      setStoredProgress(activeOrgId, { step, selectedUseCases: newValue, websiteUrl });
      return newValue;
    });
  }, [activeOrgId, step, websiteUrl]);

  const refreshSubscription = React.useCallback(async () => {
    try {
      const snapshot = await billingService.getSubscription();
      setSubscription(snapshot);
    } catch (err: any) {
      // Subscription fetch may fail for brand new users; don't show error
    }
  }, [billingService]);

  // Load billing plans (removed IS_ALPHA guard)
  React.useEffect(() => {
    if (!activeOrgId) return;

    (async () => {
      setPlansLoading(true);
      setPlansError(null);
      try {
        const data = await billingService.listPlans();
        setPlans(data);
        if (data.length) {
          setSelectedPlanKey(data[0].key);
        }
      } catch (err: any) {
        setPlansError(err?.message || 'Failed to load billing plans');
      } finally {
        setPlansLoading(false);
      }
    })();
    refreshSubscription();
  }, [billingService, refreshSubscription, activeOrgId]);

  // Fetch use cases
  React.useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch('/api/use-cases');
        const json = await res.json();
        if (json.success) {
          setUseCases(json.data);
        }
      } catch (err) {
        console.warn('Failed to load use cases:', err);
      }
    })();
  }, []);

  // Handle checkout return params
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get('checkout');
    if (status === 'success') {
      setCheckoutMessage('Checkout complete. Updating your subscription...');
      refreshSubscription().finally(() => {
        setCheckoutMessage('Subscription confirmed!');
        setTimeout(() => {
          setStep('welcome');
        }, 1500);
      });
      navigate('/onboarding', { replace: true });
    } else if (status === 'cancelled') {
      setCheckoutError('Checkout was cancelled. You can retry when ready.');
      setStep(IS_ALPHA ? 'welcome' : 'plan-selection');
      navigate('/onboarding', { replace: true });
    }
  }, [location.search, navigate, refreshSubscription]);

  React.useEffect(() => {
    if (!initialized) {
      fetchOrgs().catch(() => {});
    }
  }, [initialized, fetchOrgs]);

  // Additional fallback: if initialized but no orgs and no active org, refetch
  React.useEffect(() => {
    if (initialized && orgs.length === 0 && !activeOrgId) {
      const timer = setTimeout(() => {
        fetchOrgs().catch(() => {});
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [initialized, orgs.length, activeOrgId, fetchOrgs]);

  React.useEffect(() => {
    const active = orgs.find((o) => o.id === activeOrgId);
    let completed = Boolean(active?.onboarding_completed);
    if (!completed && active) {
      try {
        completed = localStorage.getItem(`ima_onboarding_completed_${active.id}`) === '1';
      } catch {}
    }
    if (completed) {
      navigate('/dashboard', { replace: true });
    }
  }, [orgs, activeOrgId, navigate]);

  const hasActiveSubscription = subscription ? SUBSCRIPTION_ACTIVE_STATUSES.includes(subscription.status) : false;

  const currentPlan = React.useMemo(() => {
    if (!selectedPlanKey) return null;
    return plans.find((plan) => plan.key === selectedPlanKey) ?? null;
  }, [plans, selectedPlanKey]);

  const priceForInterval = React.useMemo(() => {
    if (!currentPlan) return null;
    return currentPlan.prices.find((price) => price.interval === selectedInterval) ?? null;
  }, [currentPlan, selectedInterval]);

  const handleStartCheckout = async () => {
    if (!selectedPlanKey) return;
    // Derive interval from the selected plan's first available price
    const selectedPlan = plans.find(p => p.key === selectedPlanKey);
    const interval = selectedPlan?.prices?.[0]?.interval ?? selectedInterval;
    setCheckoutError(null);
    try {
      setCheckoutLoading(true);
      const session = await billingService.createCheckoutSession({
        planKey: selectedPlanKey,
        interval,
        source: 'onboarding'
      });
      window.location.assign(session.url);
    } catch (err: any) {
      setCheckoutError(err?.message || 'Failed to start checkout');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handlePortalOpen = async () => {
    try {
      setPortalLoading(true);
      const session = await billingService.createPortalSession(undefined, 'onboarding');
      window.open(session.url, '_blank', 'noopener');
    } catch (err: any) {
      setCheckoutError(err?.message || 'Failed to open billing portal');
    } finally {
      setPortalLoading(false);
    }
  };

  // Profile completion handler
  const handleCompleteProfile = async () => {
    setProfileError(null);
    if (profilePassword.length < 8) {
      setProfileError('Password must be at least 8 characters');
      return;
    }
    if (profilePassword !== profileConfirmPassword) {
      setProfileError('Passwords do not match');
      return;
    }
    try {
      setProfileLoading(true);
      const res = await apiFetch('/api/auth/complete-profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: profileFullName.trim(),
          password: profilePassword,
          orgName: profileOrgName.trim() || undefined,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error?.message || 'Failed to update profile');
      }
      // Refresh user data in store
      await useAuthStore.getState().fetchMe(true);
      setNeedsProfile(false);
      setStep('use-cases');
    } catch (err: any) {
      setProfileError(err?.message || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const saveUseCases = async () => {
    if (!activeOrgId) return;
    try {
      setUseCasesLoading(true);
      setUseCasesError(null);
      await apiFetch(`/api/orgs/${activeOrgId}/use-cases`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ use_cases: selectedUseCases, copy_templates: true }),
      });
      if (!IS_ALPHA && !hasActiveSubscription) {
        setStep('plan-selection');
      } else {
        setStep('welcome');
      }
    } catch (e: any) {
      setUseCasesError(e?.message || 'Failed to save use cases');
    } finally {
      setUseCasesLoading(false);
    }
  };

  const toggleUseCase = (id: string) => {
    setSelectedUseCases(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const finish = async () => {
    setStepInternal('done');
    let orgId = getActiveOrgId();
    try {
      if (!orgId) {
        try {
          await fetchOrgs();
        } catch {}
        orgId = getActiveOrgId();
      }
      if (orgId) {
        await orgService.markOnboardingComplete(orgId);
        clearStoredProgress(orgId);
        try {
          localStorage.setItem(`ima_onboarding_completed_${orgId}`, '1');
        } catch {}
        try {
          await useOrgStore.getState().fetchOrgs();
        } catch {}
      }
    } catch {
      // ignore; still continue navigation
    }
    navigate('/dashboard', { replace: true });
  };

  const stepProgress = step === 'profile' ? 20 : step === 'use-cases' ? 40 : step === 'plan-selection' ? 60 : step === 'welcome' ? 80 : 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4">
      <div className="w-full max-w-3xl">
        <div className="bg-background rounded-2xl shadow-xl overflow-hidden border">
          <div className="p-6 md:p-8 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">Let's set up your workspace</h2>
                <p className="text-sm text-muted-foreground">
                  {step === 'profile'
                    ? 'Complete your profile to get started.'
                    : step === 'plan-selection'
                    ? 'Pick a plan to power your AI creative workflows.'
                    : 'Tell us what you\'ll use Cascady for so we can customize your experience.'}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="inline-flex items-center gap-2"
                onClick={async () => {
                  await useAuthStore.getState().logout();
                  navigate('/', { replace: true });
                }}
              >
                <LogOut className="w-4 h-4" />
                <span>Sign out</span>
              </Button>
            </div>

            <Progress value={stepProgress} />

            {/* Profile Step — for passwordless users from anonymous checkout */}
            {step === 'profile' && (
              <div className="space-y-5">
                <Stack gap={3}>
                  <div className="text-center py-2">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-primary/10 rounded-full mb-3">
                      <User className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-medium">Complete your profile</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Set your name and password to secure your account.
                    </p>
                  </div>
                </Stack>

                {profileError && <Alert variant="error">{profileError}</Alert>}

                <div className="space-y-4 max-w-md mx-auto">
                  <Input
                    label="Full name"
                    value={profileFullName}
                    onChange={e => setProfileFullName(e.target.value)}
                    placeholder="John Doe"
                    required
                  />
                  <Input
                    label="Workspace name"
                    value={profileOrgName}
                    onChange={e => setProfileOrgName(e.target.value)}
                    placeholder="My Company"
                    hint="Optional — we'll use your name if blank"
                  />
                  <Input
                    label="Password"
                    type="password"
                    value={profilePassword}
                    onChange={e => setProfilePassword(e.target.value)}
                    placeholder="Choose a password"
                    minLength={8}
                    required
                    hint="At least 8 characters"
                  />
                  <Input
                    label="Confirm password"
                    type="password"
                    value={profileConfirmPassword}
                    onChange={e => setProfileConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    minLength={8}
                    required
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <Button
                    variant="primary"
                    onClick={handleCompleteProfile}
                    loading={profileLoading}
                    disabled={!profileFullName.trim() || !profilePassword || !profileConfirmPassword}
                  >
                    Continue
                  </Button>
                </div>
              </div>
            )}

            {step === 'use-cases' && (
              <div className="space-y-5">
                <Stack gap={3}>
                  <h3 className="text-lg font-medium">What will you use Cascady for?</h3>
                  <p className="text-sm text-muted-foreground">
                    Select all that apply. We'll customize your experience and add relevant starter templates.
                  </p>
                </Stack>

                {useCasesError && <Alert variant="error">{useCasesError}</Alert>}

                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {useCases.map(uc => {
                    const isSelected = selectedUseCases.includes(uc.id);
                    const IconComponent = uc.icon && iconMap[uc.icon] ? iconMap[uc.icon] : null;
                    return (
                      <button
                        key={uc.id}
                        type="button"
                        onClick={() => toggleUseCase(uc.id)}
                        className={`relative p-3 md:p-4 rounded-xl border-2 text-left transition-all flex flex-col items-start ${
                          isSelected
                            ? 'border-primary bg-primary/10 shadow-md ring-2 ring-primary/20'
                            : 'border-border hover:border-primary/50 hover:bg-muted/50'
                        }`}
                      >
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-5 h-5 md:w-6 md:h-6 rounded-full bg-primary flex items-center justify-center">
                            <CheckCircle2 className="w-3 h-3 md:w-4 md:h-4 text-white" />
                          </div>
                        )}
                        {IconComponent && (
                          <IconComponent className={`w-5 h-5 shrink-0 mb-2 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                        )}
                        <span className={`font-medium text-sm leading-tight pr-5 md:pr-6 ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                          {uc.label}
                        </span>
                        {uc.description && (
                          <p className={`text-xs leading-tight mt-1 line-clamp-2 ${isSelected ? 'text-primary/70' : 'text-muted-foreground'}`}>{uc.description}</p>
                        )}
                      </button>
                    );
                  })}
                </div>

                <div className="flex justify-end gap-2">
                  <Button variant="primary" onClick={saveUseCases} loading={useCasesLoading}>
                    Continue {selectedUseCases.length > 0 && `(${selectedUseCases.length} selected)`}
                  </Button>
                </div>
              </div>
            )}

            {step === 'plan-selection' && (
              <div className="space-y-5">
                <Stack gap={3}>
                  <div className="text-center py-2">
                    <h3 className="text-lg font-medium">Choose your plan</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Select a plan to unlock AI generation in your workspace.
                    </p>
                  </div>
                </Stack>

                {checkoutError && <Alert variant="error">{checkoutError}</Alert>}
                {checkoutMessage && <Alert variant="success">{checkoutMessage}</Alert>}

                {plansLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                  </div>
                ) : plansError ? (
                  <Alert variant="error">{plansError}</Alert>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {plans
                      .filter(p => p.isActive && p.key !== 'alpha')
                      .sort((a, b) => a.displayOrder - b.displayOrder)
                      .map(plan => {
                        const price = plan.prices[0];
                        if (!price) return null;
                        const isSelected = selectedPlanKey === plan.key;
                        const intervalLabel = price.interval === 'month' ? 'mo' : 'yr';
                        return (
                          <button
                            key={plan.id}
                            type="button"
                            onClick={() => setSelectedPlanKey(plan.key)}
                            className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                              isSelected
                                ? 'border-primary bg-primary/10 shadow-md ring-2 ring-primary/20'
                                : 'border-border hover:border-primary/50 hover:bg-muted/50'
                            }`}
                          >
                            {isSelected && (
                              <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                                <CheckCircle2 className="w-4 h-4 text-white" />
                              </div>
                            )}
                            <div className="font-medium text-foreground">{plan.name}</div>
                            <div className="text-2xl font-bold mt-1">
                              {formatCurrency(price.unitAmount, price.currency)}
                              <span className="text-sm font-normal text-muted-foreground">
                                /{intervalLabel}
                              </span>
                            </div>
                            {plan.description && (
                              <p className="text-xs text-muted-foreground mt-2">{plan.description}</p>
                            )}
                            {plan.features?.filter(f => f.isIncluded).length > 0 && (
                              <div className="mt-3 space-y-1.5">
                                {plan.features.filter(f => f.isIncluded).map((f, i) => (
                                  <div key={i} className="flex items-start gap-1.5 text-xs text-muted-foreground">
                                    <CheckCircle2 className="w-3 h-3 text-primary mt-0.5 shrink-0" />
                                    <span>{f.text}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </button>
                        );
                      })}
                  </div>
                )}

                <div className="flex justify-between items-center pt-2">
                  <Button variant="secondary" onClick={() => setStep('use-cases')}>Back</Button>
                  <Button
                    variant="primary"
                    onClick={handleStartCheckout}
                    loading={checkoutLoading}
                    disabled={!selectedPlanKey || plansLoading}
                  >
                    Subscribe & Continue
                  </Button>
                </div>
              </div>
            )}

            {step === 'welcome' && (
              <div className="space-y-5">
                <div className="text-center py-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
                    <CheckCircle2 className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">
                    {hasActiveSubscription ? 'Welcome to Cascady!' : IS_ALPHA ? 'Welcome to the Closed Alpha!' : 'Welcome to Cascady'}
                  </h3>
                </div>

                <div className="bg-muted/30 rounded-xl p-5 space-y-3 text-sm">
                  <p className="text-foreground">
                    We've set up your workspace with starter templates based on your selections. Here's what happens next:
                  </p>
                  <ul className="space-y-2 text-muted-foreground">
                    {hasActiveSubscription ? (
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                        <span>
                          Your <strong>{subscription?.plan?.name || 'subscription'}</strong> plan is active.{' '}
                          <strong>{formatNumber(4000)} credits</strong> ready to use.
                        </span>
                      </li>
                    ) : IS_ALPHA ? (
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                        <span>Explore canvases, templates, and all AI models. <strong>Start a free trial</strong> when you're ready to generate.</span>
                      </li>
                    ) : (
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                        <span>You'll receive <strong>signup credits</strong> to start generating</span>
                      </li>
                    )}
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                      <span>Build visual AI workflows on an infinite canvas</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                      <span>Save and reuse your favorite workflows as templates</span>
                    </li>
                  </ul>
                  <p className="text-muted-foreground pt-1">
                    We're excited to have you on board. Click below to enter your workspace.
                  </p>
                </div>

                <div className="flex justify-between items-center pt-2">
                  <Button variant="secondary" onClick={() => setStep(IS_ALPHA ? 'use-cases' : 'plan-selection')}>Back</Button>
                  <Button variant="primary" onClick={finish} size="lg">
                    Enter Workspace
                  </Button>
                </div>
              </div>
            )}


            {step === 'done' && <Alert variant="success">All done! Redirecting you to your dashboard...</Alert>}
          </div>
        </div>

        <div className="mt-8 text-center text-xs text-muted-foreground">
          Need help? <RouterLink to="/contact" className="underline hover:text-foreground transition-colors">Contact support</RouterLink>.
        </div>
      </div>
    </div>
  );
}
